'use client';

import dynamic from 'next/dynamic';
import React from 'react';
import { ChevronDown, PoundSterling, Scale, Signal } from 'lucide-react';
import { CalculatorTable } from '@/features/b2b/resources/components/lifetime-mortgage-calculators/annual-overpayments-and-erc-calculator/CalculatorTable';
import { useTabDropdown } from '@/hooks/useTabDropdown';

// Chart.js only loads once results are shown, so the charts stay out of the page's first download
const LoanChart = dynamic(
  () => import('@/features/b2b/resources/components/lifetime-mortgage-calculators/annual-overpayments-and-erc-calculator/LoanChart').then((mod) => ({ default: mod.LoanChart })),
  { ssr: false }
);

const EquityChart = dynamic(
  () => import('@/features/b2b/resources/components/lifetime-mortgage-calculators/annual-overpayments-and-erc-calculator/EquityChart').then((mod) => ({ default: mod.EquityChart })),
  { ssr: false }
);

const tabs = [
  {
    id: 'annual-overpayments-table',
    title: 'With / Without An Annual Plan',
    icon: <Signal className="w-5 h-5" />,
  },
  {
    id: 'loan-balance-graph',
    title: 'Loan Balance',
    icon: <Scale className="w-5 h-5" />,
  },
  {
    id: 'equity-balance-graph',
    title: 'Equity Remaining',
    icon: <PoundSterling className="w-5 h-5" />,
  },
];

export const AnnualRepaymentTabs = ({ formData, setActiveTab }) => {
  const { isOpen, selectedTab, dropdownRef, toggleDropdown, selectTab } = useTabDropdown(tabs, setActiveTab);

  return (
    <div className="mb-8">
      {/* Desktop tabs - visible on lg and up */}
      <div className="hidden lg:block border-b-2 border-[#CBCDD6] mb-8 print-button">
        <div className="flex justify-center">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              aria-label={tab.title}
              className={`text-base
                px-6 py-2
                font-medium
                transition-colors duration-200
                hover:text-[#006EB7]
                border-b-2
                -mb-0.5 ${selectedTab.id === tab.id ? 'text-[#006EB7] border-[#006EB7]' : 'text-[#373945] border-transparent'}`}
              onClick={() => selectTab(tab)}
            >
              <div className="flex items-center">
                <span className="mr-2">{tab.icon}</span>
                <span>{tab.title}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile dropdown - visible on screens smaller than lg */}
      <div className="lg:hidden relative print-button" ref={dropdownRef}>
        <button
          onClick={toggleDropdown}
          className="flex items-center justify-between w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="flex items-center">
            <span className="mr-2">{selectedTab.icon}</span>
            <span>{selectedTab.title}</span>
          </div>
          <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <ul
              className="py-1 max-h-60 overflow-auto"
              role="listbox"
              aria-labelledby="dropdown-button"
            >
              {tabs.map((tab) => (
                <li
                  key={tab.id}
                  className={`flex items-center px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                    selectedTab.id === tab.id ? 'bg-blue-100' : ''
                  }`}
                  onClick={() => selectTab(tab)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      selectTab(tab);
                    }
                  }}
                  role="option"
                  aria-selected={selectedTab.id === tab.id}
                  tabIndex={0}
                  aria-label={tab.title}
                >
                  <span className="mr-2">{tab.icon}</span>
                  <span>{tab.title}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Tab content */}
      <div className={`${selectedTab.id === 'annual-overpayments-table' ? 'block' : 'hidden'} print:block print:mb-8`}>
        <CalculatorTable dataRemainingBalanceTable={formData.dataRemainingBalanceTable} />
      </div>

      <div className={`${selectedTab.id === 'loan-balance-graph' ? 'block' : 'hidden'} print:block print:mb-8`}>
        <LoanChart
          loanBalanceGraphDataWithOutPayment={formData.loanBalanceGraphDataWithOutPayment}
          loanBalanceGraphDataWithPayment={formData.loanBalanceGraphDataWithPayment}
        />
      </div>

      <div className={`${selectedTab.id === 'equity-balance-graph' ? 'block' : 'hidden'} print:block print:mb-8`}>
        <EquityChart
          equityRemainingGraphDataWithoutPayment={formData.equityRemainingGraphDataWithoutPayment}
          equityRemainingGraphDataWithPayment={formData.equityRemainingGraphDataWithPayment}
        />
      </div>
    </div>
  );
};
